import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
  DateRangePicker as RangePicker,
  defaultInputRanges,
  defaultStaticRanges,
} from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DAM_SVG } from 'components/Dam/DamAssets';
import { ButtonC } from 'Common/Buttons';
import './DateRangePicker.scss';

const BRAND_COLOR = '#753cb7';
const RANGE_KEY = 'selection';

const toRange = (startDate, endDate) => {
  const start = startDate ? new Date(startDate) : new Date();
  const end = endDate ? new Date(endDate) : start;
  return { startDate: start, endDate: end, key: RANGE_KEY };
};

/**
 * Input that opens a range picker: preset rows on the left, calendar on the right.
 * Picks are drafted inside the popup and only handed to the parent on Apply, so a
 * half-finished selection (start picked, end not yet) never reaches the caller.
 *
 * The first four props match the previous two-calendar version, so existing callers
 * keep working unchanged. Everything after them is opt-in:
 *  - `maxDate` / `minDate`: share-link expiry needs future dates, analytics does not
 *  - `label` / `title`: pass null to drop the field label or the popup heading
 *  - `size="sm"`: a 36px trigger that sits in a row of compact controls
 *  - `align="end"`: opens the popup against the trigger's right edge, for a trigger sitting
 *    near the right of the page
 *  - `isOpen` + `onOpenChange`: let the parent open the popup (e.g. from a "Custom" tab);
 *    leave `isOpen` undefined and the input manages it on its own
 */
const DateRangePicker = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  label = 'Date Range',
  title = 'Select Date-Range',
  placeholder = 'DD/MM/YYYY - DD/MM/YYYY',
  size = 'md',
  align = 'start',
  minDate,
  maxDate,
  isOpen: controlledOpen,
  onOpenChange,
}) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? controlledOpen : internalOpen;
  // null until the user picks something; until then the popup shows the committed range
  const [draft, setDraft] = useState(null);
  const [focusedRange, setFocusedRange] = useState([0, 0]);
  // wraps the input AND the popup, so clicking the input toggles instead of
  // closing on mousedown and reopening on click
  const wrapperRef = useRef(null);

  const setOpen = (next) => {
    if (!isControlled) setInternalOpen(next);
    if (onOpenChange) onOpenChange(next);
  };
  // the document listeners below outlive a render; read the current setter through a ref
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;

  // A closed picker forgets unapplied picks, however it was closed: outside click, Esc,
  // Apply, or the parent.
  useEffect(() => {
    if (!isOpen) {
      setDraft(null);
      setFocusedRange([0, 0]);
    }
  }, [isOpen]);

  // "days starting today" only ever produces future dates, which a maxDate rejects
  const inputRanges = useMemo(
    () =>
      maxDate
        ? defaultInputRanges.filter((range) => range.label !== 'days starting today')
        : defaultInputRanges,
    [maxDate],
  );

  // Presets like "This Month" run to the end of the month; keep them inside the limits
  const clamp = (date) => {
    if (maxDate && moment(date).isAfter(maxDate, 'day')) return new Date(maxDate);
    if (minDate && moment(date).isBefore(minDate, 'day')) return new Date(minDate);
    return date;
  };

  const shownRange = draft ?? toRange(startDate, endDate);

  const handleChange = (ranges) => {
    const { startDate: nextStart, endDate: nextEnd } = ranges[RANGE_KEY];
    setDraft({ startDate: clamp(nextStart), endDate: clamp(nextEnd), key: RANGE_KEY });
  };

  const handleApply = () => {
    setStartDate(shownRange.startDate);
    setEndDate(shownRange.endDate);
    setOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpenRef.current(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpenRef.current(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const displayValue =
    startDate && endDate
      ? `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format('DD/MM/YYYY')}`
      : startDate
        ? moment(startDate).format('DD/MM/YYYY')
        : '';

  return (
    <div
      className={`dam_date_range ${size === 'sm' ? 'dam_date_range_sm' : ''} ${
        align === 'end' ? 'dam_date_range_end' : ''
      }`}
      ref={wrapperRef}
    >
      {label && <label className="custom-label mb-0">{label}</label>}

      <div className="dam_date_range_trigger">
        <span className="dam_date_range_icon">{DAM_SVG.calendarSvg}</span>
        <input
          type="text"
          readOnly
          value={displayValue}
          placeholder={placeholder}
          className="lg-input Custom-input dam_date_range_input"
          aria-haspopup="dialog"
          onClick={() => setOpen(!isOpen)}
        />
      </div>

      {isOpen && (
        <div
          className="dam_date_range_popup"
          role="dialog"
          aria-label={title || 'Select date range'}
        >
          {title && <div className="dam_date_range_title">{title}</div>}

          <RangePicker
            ranges={[shownRange]}
            onChange={handleChange}
            focusedRange={focusedRange}
            onRangeFocusChange={setFocusedRange}
            rangeColors={[BRAND_COLOR]}
            moveRangeOnFirstSelection={false}
            retainEndDateOnFirstSelection
            editableDateInputs={false}
            months={1}
            direction="horizontal"
            dateDisplayFormat="MMM d, yyyy"
            staticRanges={defaultStaticRanges}
            inputRanges={inputRanges}
            {...(minDate && { minDate })}
            {...(maxDate && { maxDate })}
          />

          <div className="dam_date_range_footer">
            <ButtonC text="Apply" color="Primary" size="lg" click={handleApply} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
